import React, { useMemo } from 'react';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { Message } from '@blink/components/src/constants/messages';

import { SupplyAmountField } from './SupplyAmountField';

import { Store } from '@/shared/store';
import { showModalAction } from '@/shared/store/modals/actions';
import { Button, Text, TipCard, TooltipWrapper } from '@/shared/ui';
import { formatWithCommas, formatWithMaxPrecision } from '@/shared/utils/helpers';
import { useModal, useSupply } from '@/features/Modals/hooks';
import SupplyModalSchema, { SupplyModalData } from '@/shared/validations/supplyModal.schema';
import { ModalName } from '@/shared/store/modals/constants';
import { SummaryRow } from '@/widgets/Modals';
import { OperationStatusType } from '@/features/Modals/ui/OperationStatusModal';
import { useMaxBalance } from '@/features/Modals/ui/Vaults/Supply/hooks/useMaxBalance';

const SupplyModalTexts = {
    title: 'How much do you want to supply?',
    tip: 'Arkis Liquidity (LP) tokens automatically earn interest proportional to your share of the pool.',
    tooltips: {
        vaultUtilization: 'Total token supply being used by borrowers for their operations',
        vaultBalance:
            'Vault Balance shows the available tokens that have not been used by borrowers',
    },
};

enum VaultsSupplyModalFields {
    AMOUNT = 'amount',
}

export const VaultsSupplyModal = () => {
    const walletAddress = useSelector((state: Store) => state.wallets.active.address);
    const dispatch = useDispatch();
    const { modalProps } = useModal();
    const {
        tokenSymbol,
        totalDepositThreshold,
        decimals,
        poolUtilization,
        totalDeposited,
        refresh,
        netApy,
    } = modalProps || {};

    const maxBalance = useMaxBalance();

    const methods = useForm<SupplyModalData>({
        resolver: yupResolver(SupplyModalSchema),
        reValidateMode: 'onChange',
        context: { maxBalance },
    });

    const { makeSupply } = useSupply();

    const currentModal = {
        activeModal: ModalName.VAULT_SUPPLY_MODAL,
        modalTitle: 'Supply Liquidity',
        modalProps,
    };

    const onSubmit = async (data: SupplyModalData) => {
        if (!walletAddress) {
            dispatch(
                showModalAction({
                    modalName: ModalName.CONNECT_WALLET_MODAL,
                    modalTitle: 'Connect Wallet',
                    previousModal: currentModal,
                }),
            );
            return;
        }

        dispatch(
            showModalAction({
                modalName: ModalName.OPERATION_STATUS_MODAL,
                modalProps: {
                    type: OperationStatusType.Pending,
                    description: Message.PENDING_TEXT,
                },
                previousModal: currentModal,
            }),
        );

        await makeSupply({
            amount: data[VaultsSupplyModalFields.AMOUNT],
            agreementAddress: modalProps?.agreementAddress,
            tokenAddress: modalProps?.tokenAddress,
        });

        if (refresh) {
            await refresh();
        }
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} className='w-[448px] pt-7'>
                <Text className='mb-3 mt-2'>{SupplyModalTexts.title}</Text>
                <SupplyAmountField name={VaultsSupplyModalFields.AMOUNT} />

                <div className='mt-5'>
                    <TipCard text={SupplyModalTexts.tip} title='Tip' />
                </div>

                <div className='mt-4 border-b pb-4 border-border-opaque'>
                    <SummaryRow
                        label='Net APY'
                        value={Number.isNaN(netApy) ? 'N/A' : `${netApy} %`}
                    />
                    <SummaryRow label='Vault Utilization' value={`${poolUtilization}%`}>
                        <TooltipWrapper text={SupplyModalTexts.tooltips.vaultUtilization} />
                    </SummaryRow>
                    <SummaryRow label='Vault Balance' className='border-none'>
                        <div className='flex gap-2'>
                            <Text
                                type={Text.types.Paragraph}
                                fontWeight={Text.weights.SemiBold}
                            >{`${formatWithCommas(
                                formatWithMaxPrecision(totalDeposited, decimals),
                            )} ${tokenSymbol}`}</Text>
                            <Text
                                type={Text.types.Paragraph}
                                className='text-content-secondary'
                            >{`/ ${formatWithCommas(totalDepositThreshold)} ${tokenSymbol}`}</Text>
                        </div>
                        <TooltipWrapper text={SupplyModalTexts.tooltips.vaultBalance} />
                    </SummaryRow>
                </div>

                <SubmitButton />
            </form>
        </FormProvider>
    );
};

function SubmitButton() {
    const walletAddress = useSelector((state: Store) => state.wallets.active.address);
    const { watch, getFieldState } = useFormContext();
    const { modalProps } = useModal();
    const { tokenSymbol } = modalProps || {};
    const amount = watch(VaultsSupplyModalFields.AMOUNT);
    const { error } = getFieldState(VaultsSupplyModalFields.AMOUNT);

    const maxBalance = useMaxBalance();

    const getSubmitButtonText = () => {
        return !walletAddress ? 'Connect wallet' : `Approve ${tokenSymbol}`;
    };

    const isDisabled = useMemo(() => {
        if (error || !amount || Number(amount) === 0) return true;
        return Number(maxBalance) < Number(amount);
    }, [amount, maxBalance, walletAddress, error]);

    return (
        <Button
            size={Button.sizes.Large}
            disabled={isDisabled}
            className='w-full font-semibold mt-7'
            type='submit'
        >
            {getSubmitButtonText()}
        </Button>
    );
}
